import * as React from 'react'
// utils
import { handleError } from 'utils/errors/handlers'
import {
  getEntityIdKey,
  reserveReferencedInsertPosition,
  TSourceItem
} from 'containers/MinimizedObjectivesList/layers/Business/utils'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
import { MinimizedObjectivesListAPIContext } from 'containers/MinimizedObjectivesList/layers/API'

interface IDescriptor {
  entityType: string
  entityId: string
  treePosition?: number
  planId: string
  // source-plan order used to keep referenced items in sync
  sourceItems: TSourceItem[]
  // id of the source plan item — tracked so it can be removed later
  sourcePlanItemId: string
}

interface IArgs {
  selectedPlanItems: any[]
  selectPlanItem: (items: any[]) => void
}

/**
 * Shared "add referenced item" flow used by both the tree menu and the details
 * modal: resolves the slot (shifting siblings), creates the item, refreshes the
 * trees and records the created id against its source plan item.
 *
 * `selectedPlanItems`/`selectPlanItem` are injected because the tree-menu container
 * owns that state locally, while the details-modal container reads it from context —
 * both point at the same list.
 */
export const useAddReferencedItem = ({ selectedPlanItems, selectPlanItem }: IArgs) => {
  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)
  const { createPlanItemMutation, updatePlanTreeMutation, refetchPlanItems, planItems } = React.useContext(
    MinimizedObjectivesListAPIContext
  )

  return async ({ entityType, entityId, treePosition, planId, sourceItems, sourcePlanItemId }: IDescriptor) => {
    const entityIdKey = getEntityIdKey(entityType as any)

    if (!entityIdKey) {
      return
    }

    try {
      const insertPosition = await reserveReferencedInsertPosition({
        node: { entityId, treePosition },
        destinationItems: (planItems as any) || [],
        sourceItems: sourceItems || [],
        updatePlanTreeMutation
      })

      const {
        data: {
          planItemCreate: {
            data: { id }
          }
        }
      } = await createPlanItemMutation({
        variables: {
          input: { planId, treePosition: insertPosition, referenced: true, [entityIdKey]: entityId }
        }
      })

      await treeQueryRefetch?.()
      // keep the modal's plan items in sync so a following add resolves its position against fresh data
      await refetchPlanItems?.()

      selectPlanItem([...selectedPlanItems, { prevId: sourcePlanItemId, newId: id }])
    } catch (errors) {
      handleError(errors, 'useAddReferencedItem')
    }
  }
}
